import { SpanStatusCode, trace } from '@opentelemetry/api'
import { Component, ErrorInfo, isValidElement, ReactNode } from 'react'

import { APP_NAME } from '../../config/costants'
import { isFunction } from '../../utils/is'
import { ERROR_BOUNDARY_INITIAL_STATE } from './const'
import { ErrorBoundaryProps, ErrorBoundaryState } from './types'

/**
 * The `ErrorBoundaryWrapper` class is a React component that handles errors in a component tree and provides fallback UI when an error occurs.
 */
export class ErrorBoundaryWrapper extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = ERROR_BOUNDARY_INITIAL_STATE

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this)
  }

  /**
   * This function creates a new error object with the component stack trace added to the error stack.
   * @param {Error} error - The error object that was caught by a React ErrorBoundary component.
   * @param {ErrorInfo} errorInfo - `errorInfo` is an object that contains information about the
   * component that threw the error. It has the following properties:
   * @returns a new Error object with the same message as the original error, but with a modified name
   * and stack trace. The name of the new error includes "React ErrorBoundary" and the name of the
   * original error. The stack trace of the new error is set to the component stack trace provided in the
   * errorInfo parameter.
   */
  getErrorWithComponentStack(error: Error, errorInfo: ErrorInfo): Error {
    const newError = new Error(error.message)
    newError.name = `React ErrorBoundary ${error.name}`
    newError.stack = errorInfo.componentStack ?? undefined
    return newError
  }

  /**
   * This is a static method in a React component that returns an object with a boolean flag indicating
   * if an error has occurred and the error object itself.
   * @param {Error} error - The `error` parameter is an instance of the `Error` class that represents the
   * error that was thrown in the component tree below the `ErrorBoundary` component. The
   * `getDerivedStateFromError` method is a static method that is called when an error is thrown during
   * rendering, and it returns
   * @returns A new state object with two properties: "hasError" set to true and "error" set to the error
   * object passed as an argument.
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    }
  }

  /**
   * This function handles errors in a React component and logs them with tracing information.
   * @param {Error} error - The error that was thrown and caught by the componentDidCatch method. It is
   * an instance of the Error class or one of its subclasses.
   * @param {ErrorInfo} errorInfo - ErrorInfo is an interface provided by React that contains information
   * about the component stack at the time of the error, including the component name, file name, and
   * line number. It is passed as the second parameter to the componentDidCatch method.
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorWithComponentStack = this.getErrorWithComponentStack(error, errorInfo)

    this.props.beforeCapture?.(errorWithComponentStack)

    const tracer = trace.getTracer(APP_NAME)
    // Create a span. A span must be closed.
    tracer.startActiveSpan(errorWithComponentStack.name, (span) => {
      span.recordException(error)
      span.setStatus({ code: SpanStatusCode.ERROR })
      span.end()
    })
    this.props.onError?.(errorWithComponentStack)
    this.setState({ hasError: true, error })
  }

  /**
   * This is a lifecycle method in a React component that calls a function passed as a prop when the
   * component mounts.
   */
  override componentDidMount(): void {
    this.props.onMount?.()
  }

  /**
   * This function calls a callback function passed as a prop when the component is unmounted, passing in
   * the current state's error as an argument if it exists.
   */
  override componentWillUnmount(): void {
    this.props.onUnmount?.(this.state.error)
  }

  /**
   * This function resets the error boundary state and calls the onReset function with the current error
   * state as an argument.
   */
  resetErrorBoundary(): void {
    this.props.onReset?.(this.state.error)
    this.setState(ERROR_BOUNDARY_INITIAL_STATE)
  }
  /**
   * This function renders either the children or a fallback element based on whether an error has
   * occurred.
   * @returns The `render()` method is returning either the `children` prop or the `fallback` prop
   * depending on whether an error has occurred or not. If `hasError` is true, it will return the
   * `fallback` prop. If the `fallback` prop is a function, it will call that function with the error and
   * a `resetErrorBoundary` function as arguments. If the `fallback`
   */
  override render(): ReactNode {
    if (!this.state.hasError) {
      return isFunction(this.props.children) ? this.props.children() : this.props.children
    }

    const element = !isFunction(this.props.fallback)
      ? this.props.fallback
      : this.props.fallback(this.state.error as Error, this.resetErrorBoundary)

    if (isValidElement(element)) {
      return element
    }

    if (this.props.fallback) {
      console.warn('ErrorBoundary\n', 'Cannot get a valid ReactElement from "fallback"')
    }

    return null
  }
}
